<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import GuardedView from './GuardedView.vue'
import SideNav from './shell/SideNav.vue'
import TopBar from './shell/TopBar.vue'
import PageHeader from './shell/PageHeader.vue'
import HelpDialog from './shell/HelpDialog.vue'
import { can } from '@/lib/auth'
import { landingFor } from './nav'
import { dockWidth } from './dock'
import GuideChecklist from '@/features/onboarding/GuideChecklist.vue'
import IntroTips from '@/features/onboarding/IntroTips.vue'
import LateBanner from '@/features/billing/LateBanner.vue'
import { canFollowGuide, useGuide, useGuideAction } from '@/features/onboarding/onboarding'

const KEY = 'tsh-nav-collapsed'
const readCollapsed = (): boolean => { try { return localStorage.getItem(KEY) === '1' } catch { return false } }
const collapsed = ref(readCollapsed())
const mobileOpen = ref(false)
const help = ref(false)

function toggleCollapsed() {
  collapsed.value = !collapsed.value
  try { localStorage.setItem(KEY, collapsed.value ? '1' : '0') } catch { /* kept for this session */ }
}
const route = useRoute()
const router = useRouter()

/*
 * The getting-started guide. A company admin who never saw the welcome page
 * is taken there once per visit; the checklist then follows them. Other
 * accounts get their first-login tips. Both reopen from the help.
 */
const guide = useGuide()
const guideAction = useGuideAction()
const tipsAgain = ref(false)
let welcomed = false
// The guide and the page both decide (found on the first production deploy, 2026-10-05): at a first
// sign-in the guide could arrive while the app was still on its way to the home screen, and the
// welcome page then opened only at the next sign-in.
watch(() => [guide.data.value?.welcomeSeen, route.path, landingFor(can)] as const, ([seen, path, home]) => {
  // Only in place of the home screen: a link to a given page keeps its page.
  if (seen === false && !welcomed && path === home) { welcomed = true; void router.replace('/welcome') }
}, { immediate: true })
function reopenGuide() { help.value = false; guideAction.mutate('reopen') }
function showTips() { help.value = false; tipsAgain.value = true }
const { t } = useI18n()
const screen = computed(() => (route.meta.navKey ? t(`nav.${route.meta.navKey}.label`) : undefined))
// Navigating closes the mobile drawer.
watch(() => route.fullPath, () => { mobileOpen.value = false })
/** Escape gives the focus back to the burger that opened the drawer. */
function closeMobile(viaKeyboard: boolean) {
  mobileOpen.value = false
  if (viaKeyboard) document.getElementById('shell-burger')?.focus()
}
</script>

<template>
  <!-- The menu folds and unfolds: its column's width moves rather than jumps. -->
  <div class="grid min-h-full transition-[grid-template-columns] duration-slow ease-out lg:grid-cols-[var(--cols)]"
       :style="{ '--cols': collapsed ? 'var(--nav-w-collapsed) minmax(0,1fr)' : 'var(--nav-w) minmax(0,1fr)' }">
    <div class="max-lg:contents lg:sticky lg:top-0 lg:h-screen">
      <SideNav :collapsed="collapsed" :mobile-open="mobileOpen" @toggle-collapsed="toggleCollapsed" @close-mobile="closeMobile" />
    </div>
    <div v-if="mobileOpen" class="fixed inset-0 z-[49] animate-fade-in bg-foreground/25 lg:hidden" aria-hidden="true" @click="mobileOpen = false" />
    <div class="flex min-w-0 flex-col">
      <TopBar :nav-open="mobileOpen" @open-nav="mobileOpen = !mobileOpen" @help="help = true" />
      <!-- A panel docked at the end (the test call): the header and the page make room for it. -->
      <div class="flex min-w-0 flex-1 flex-col transition-[padding] duration-slow ease-out"
           :style="dockWidth ? { paddingInlineEnd: `${dockWidth}px` } : undefined">
        <LateBanner v-if="can('billing.view')" />
        <PageHeader />
        <main class="min-w-0 flex-1 px-6 pb-8 max-sm:px-4"><GuardedView /></main>
      </div>
    </div>
    <HelpDialog :open="help" :screen="screen" :guide="canFollowGuide()" @close="help = false" @guide="reopenGuide" @tips="showTips" />
    <GuideChecklist v-if="canFollowGuide()" />
    <IntroTips v-else :again="tipsAgain" @closed="tipsAgain = false" />
  </div>
</template>
